import {
  AcceptCampaignParams,
  CampaignDetail,
  CampaignOperation,
  CampaignOperationStatus10dlc,
  CampaignOperationStatusRcs,
  CampaignSharingChain,
  CampaignListItem,
  CampaignVerification,
  RejectCampaignParams,
  RemoveExclusionResult,
  RemoteList,
  PhoneNumberHistoryParams,
  PhoneNumberHistory,
} from './types';
import {
  CampaignOperationItem,
  MigrateCnpInfo,
} from '@/features/campaigns/types';
import {
  globalDeleteService,
  globalGetService,
  globalPostService,
  globalPutService,
} from '@/utils/globalApiServices';
import { getTimezone } from '@/utils/time';

export const acceptCampaignAsPrimaryDca = async (
  campaignUid: string,
  params?: AcceptCampaignParams
) => {
  const response = await globalPutService(
    `dca/campaigns/primaryDca/${campaignUid}`,
    params
  );
  return response?.ok ? response.data : undefined;
};

export const rejectCampaignAsPrimaryDca = async (
  campaignUid: string,
  params: RejectCampaignParams
) => {
  const response = await globalDeleteService(
    `dca/campaigns/primaryDca/${campaignUid}`,
    params
  );
  return response?.ok ? response.data : undefined;
};

export const acceptCampaignAsSecondaryDca = async (
  campaignUid: string,
  params?: AcceptCampaignParams
) => {
  const response = await globalPutService(
    `dca/campaigns/secondaryDca/${campaignUid}`,
    params
  );
  return response?.ok ? response.data : undefined;
};

export const rejectCampaignAsSecondaryDca = async (
  campaignUid: string,
  params: RejectCampaignParams
) => {
  const response = await globalDeleteService(
    `dca/campaigns/secondaryDca/${campaignUid}`,
    params
  );
  return response?.ok ? response.data : undefined;
};

export const getPrimaryDcaCampaigns = async (
  params?: Record<string, unknown>
): Promise<RemoteList<CampaignListItem> | null> => {
  const response = await globalGetService(`dca/campaigns/primaryDca`, params);
  return response?.ok ? (response.data as RemoteList<CampaignListItem>) : null;
};

export const getSecondaryDcaCampaigns = async (
  params?: Record<string, unknown>
): Promise<RemoteList<CampaignListItem> | null> => {
  const response = await globalGetService(`dca/campaigns/secondaryDca`, params);
  return response?.ok ? (response.data as RemoteList<CampaignListItem>) : null;
};

export const getCampaignOperations = async (
  campaignUid: string,
  provisional?: boolean
) => {
  const response = await globalGetService(
    `dca/campaigns/${campaignUid}/operations`,
    { provisional }
  );
  return response?.ok ? (response.data as CampaignOperationItem[]) : undefined;
};

export const getOperations = async (
  campaignUid: string
): Promise<CampaignOperation[]> => {
  const response = await globalGetService(
    `dca/campaigns/${campaignUid}/operations`
  );
  if (response?.ok && Array.isArray(response.data)) {
    const parseJson = (val: string | null) => {
      try {
        if (typeof val === 'string') return JSON.parse(val);
        return val;
      } catch {
        return val;
      }
    };
    return response.data?.map((d) => {
      const {
        rulesEngineResultsJson,
        osrAttributesJson,
        checklistJson,
        ...restProps
      } = d;
      return {
        ...restProps,
        rulesEngineResultsJson: parseJson(rulesEngineResultsJson),
        osrAttributesJson: parseJson(osrAttributesJson),
        checklistJson: parseJson(checklistJson),
      };
    });
  }
  return response?.ok ? response.data : [];
};

export const electSecondaryDca = async (
  pathVariables: { campaignUid: string; electedDcaUid: string },
  params: { mnoIds: number[]; isProvisional?: boolean }
) => {
  const { campaignUid, electedDcaUid } = pathVariables ?? {};
  const response = await globalPutService(
    `dca/campaigns/primaryDca/${campaignUid}/secondaryDca/${electedDcaUid}`,
    params
  );
  return response?.ok ? response.data : undefined;
};

export const getCampaignDetail = async (
  campaignUid: string,
  params?: Record<string, unknown>
) => {
  const response = await globalGetService(
    `dca/campaigns/${campaignUid}`,
    params
  );
  return response?.ok ? (response?.data as CampaignDetail) : undefined;
};

export const updateCampaignOperationStatus = async (
  pathVariables: {
    campaignUid: string;
    networkId: number;
    status: CampaignOperationStatus10dlc | CampaignOperationStatusRcs;
  },
  params?: { explanation?: string; suspensionCategory?: string }
): Promise<CampaignOperation | undefined> => {
  const { campaignUid, networkId, status } = pathVariables;
  const response = await globalPostService(
    `dca/campaigns/${campaignUid}/network/${networkId}/status/${status}`,
    params
  );
  return response?.ok ? response.data : undefined;
};

export const getMigrateCnp = async (
  campaignUid: string,
  params?: Record<string, unknown>,
  configs?: Record<string, unknown>
) => {
  try {
    const response = await globalGetService(
      `dca/campaigns/${campaignUid}/migrateCnp`,
      params,
      configs ?? { silent: false }
    );
    return response?.ok ? (response.data as MigrateCnpInfo) : undefined;
  } catch {
    return undefined;
  }
};

export const getCampaignRoles = async (campaignUid: string) => {
  const response = await globalGetService(`dca/campaigns/${campaignUid}/roles`);
  return response?.ok ? response.data : undefined;
};

export const getSharingChain = async (
  campaignUid: string,
  params?: { provisional?: boolean }
): Promise<CampaignSharingChain | undefined> => {
  const response = await globalGetService<CampaignSharingChain>(
    `dca/campaigns/${campaignUid}/sharingChain`,
    params
  );
  return response?.ok ? response?.data : undefined;
};

export const getVerification = async (
  campaignUid: string,
  params: { agentId: string },
  configs?: Record<string, unknown>
): Promise<CampaignVerification[]> => {
  const response = await globalGetService(
    `dca/campaigns/${campaignUid}/verification`,
    params,
    configs
  );

  const parseJson = (val: string | null) => {
    try {
      if (typeof val === 'string') return JSON.parse(val);
      return val;
    } catch {
      return val;
    }
  };
  return response?.ok
    ? Array.isArray(response.data)
      ? (response.data.map((d) => ({
          ...d,
          fullReport: parseJson(d.fullReport),
          reasons: parseJson(d.reasons),
          isOwner: !!d.vettingId,
        })) as CampaignVerification[])
      : []
    : [];
};

export const removeMnoExclusion = async (
  campaignUid: string,
  mnoId: number,
  explanation: string
) => {
  const response = await globalDeleteService(
    `dca/campaigns/${campaignUid}/excludedMno/${mnoId}`,
    { explanation }
  );
  return response?.ok ? (response.data as RemoveExclusionResult) : undefined;
};

export const getPhoneNumberHistory = async (
  phone: string,
  params: PhoneNumberHistoryParams
): Promise<RemoteList<PhoneNumberHistory> | undefined> => {
  const response = await globalGetService<RemoteList<PhoneNumberHistory>>(
    `dca/campaigns/byPhone/${phone}/campaign/history`,
    {
      ...params,
      timezone: getTimezone(),
    },
    { silent: true }
  );
  return response?.ok ? response.data : undefined;
};
